/* Dynamic Plot code copyright 2018 (c) Tuva Labs Inc. */
/**
 * Plot Function Element: model and controller for a function line/curve in a plot
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by cmiller
 */

import { PlotElement } from './plotElement'
import Raphael from "raphael";

//============================
export const wrapDynPlotFunctionElement = function( Dyn ) {
    let mediator = Dyn.mediator;
    // indices used by arrays of FunctionPointData
    const kHoverPointIndex = 0, // the point under the mouse when hovering
        kClickPointIndex = 1, // the point under the mouse when last clicked
        kYInterceptPointIndex = 2; // the Y-intercept point
        // roots and other calculated point indexes are 2+

    /** a class to hold data for a visible point on a function line/curve (on user click or hover) */
    class FunctionPointData {
        constructor( x, y, pointIndex ) {
            this.wantVisible = false;
            this.isValid = false; // data and view points are valid
            this.isHoverPoint = (pointIndex === kHoverPointIndex);
            this.isClickPoint = (pointIndex === kClickPointIndex);
            this.isIntercept = (pointIndex >= kYInterceptPointIndex);
            this.dataX = x;
            this.dataY = y;
            this.viewX = this.viewY = -1; // location while hidden or invalid
            this.display = null; // {x,y} display values if valid point
            this.el = null; // reference to Raphael element
        }

        copyData( otherPt ) {
            this.wantVisible = otherPt.wantVisible;
            this.isValid = otherPt.isValid
            this.dataX = otherPt.dataX;
            this.dataY = otherPt.dataY;
            this.viewX = otherPt.viewX;
            this.viewY = otherPt.viewY;
            this.display = null; // {x,y} display values if valid point
        }

        updateViewCoords( xAxisView, yAxisView ) {
            this.isValid = Dyn.mathUtil.isFinite( this.dataY );
            if( this.isValid ) {
                this.viewX = xAxisView.dataToPixel( this.dataX );
                this.viewY = yAxisView.dataToPixel( this.dataY );
            }
            delete this.display; // old display strings out of date
        }

        setElement( circleElement ) {
            // cross-reference from point data to Raphael element and back
            this.el = circleElement;
            circleElement.data( "pointData", this );
        }

        isElementValidOrNull() {
            // return true if point data to raphael element link is correct
            let isNull = !(this.el);
            return isNull || (this === this.el.data( "pointData" ));
        }

        show() {
            this.wantVisible = true;
            if( this.el ) {
                this.el.show();
                this.el.toFront();
            }
        }

        hide() {
            this.wantVisible = false;
            if( this.el ) {
                this.el.hide();
            }
        }

        isChangedPoint( element ) {
            // true if our point data not the same as the visible circle element
            let isVisible = Dyn.plotUtil.isElementVisible( element ),
                ex = element.attr( "cx" ),
                ey = element.attr( "cy" );
            return (isVisible != this.wantVisible ||
                ex !== this.viewX ||
                ey !== this.viewY);
        }

        updateVisibility( isSelectedFormula ) {
            let showIt;
            if( this.isIntercept ) {
                showIt = isSelectedFormula && this.isValid; // show valid intercepts of selected formula
            } else {
                showIt = isSelectedFormula && this.wantVisible; // maintain visibility of others if selected formula
            }
            if( showIt ) {
                this.show();
            } else {
                this.hide();
            }
        }

        updateDisplay( xAttr, yAttr, xPrecision, yPrecision ) {
            // update string values for Summary View or hover over point
            this.display = { // works even if invalid point
                x: xAttr.axisValueToString( this.dataX, xPrecision ),
                y: yAttr.axisValueToString( this.dataY, yPrecision )
            };
        }
    }

    /** a class track the X, Y min/max range values of an evaluated function curve */
    class FormulaRange {
        constructor() {
            // the actual range of valid x, y numbers of the evaluated function, point by point
            this.evalX = null; // new Dyn.mathUtil.Range( x, x );
            this.evalY = null; // new Dyn.mathUtil.Range( y, y );
            // and in view coordinates
            this.viewX = null; // new Dyn.mathUtil.Range( x, x );
            this.viewY = null; // new Dyn.mathUtil.Range( y, y );
            // the actual range as a string to report to the Model Functions panel
            this.stringX = "";
            this.stringY = "";
            // stats about the evaluated function
            this.countFinite = 0; // finite points
            this.countNaN = 0;    // undefined points (have x but no y)
            this.countAll = 0;    // total number of points evaluated
            this.hasDiscontinuity = false;
            this.lastMessage = "";
        }

        /**
         * Add a point on the curve to expand the given formula's computed numeric range
         * @param x -- x value of point in data coordinates
         * @param y -- y value of point in data coordinates
         * @param hasDiscontinuity -- true if this point is discontinuous with the previous point.
         */
        addToRange( x, y, hasDiscontinuity, viewX, viewY ) {
            if( !this.evalX ) {
                // all points invalid until now, create first point
                this.evalX = new Dyn.mathUtil.Range( x, x );
                this.evalY = new Dyn.mathUtil.Range( y, y );
                this.viewX = new Dyn.mathUtil.Range( viewX, viewX );
                this.viewY = new Dyn.mathUtil.Range( viewY, viewY );
            } else {
                this.evalX.addPoint( x );
                this.evalY.addPoint( y );
                this.viewX.addPoint( viewX );
                this.viewY.addPoint( viewY );
            }
            this.countFinite++;
            this.countAll++;
            if( hasDiscontinuity ) {
                this.hasDiscontinuity = true; // set once and remember
            }
        }

        /** for efficiency testing, record a point on curve which does not evaluate to a defined number */
        addUndefinedPoint() {
            this.countNaN++;
            this.countAll++;
        }

        /** Update the given formula's range string, for Model Functions panel. */
        formatRange( xAxisView, yAxisView ) {
            function formatIt( attribute, range, axis ) {
                let str = "";
                if( attribute && range && range.isFinite() ) {
                    str = Dyn.loc( "{{min}} ≤ {{axis}} ≤ {{max}}", {
                        min: attribute.axisValueToString( range.min, attribute.getNumericDecimals() ),
                        max: attribute.axisValueToString( range.max, attribute.getNumericDecimals() ),
                        axis: axis
                    } );
                }
                return str;
            }

            let xAttr = xAxisView.getAttribute(),
                yAttr = yAxisView.getAttribute();
            this.stringX = formatIt( xAttr, this.evalX, 'x' );
            this.stringY = formatIt( yAttr, this.evalY, 'y' );
        }

        isFinite() {
            return this.countFinite > 0;
        }
    } // class FormulaRange

    class PlotRange {
        constructor( xAxisView, yAxisView ) {
            this.dataX = xAxisView.getNumericAxisRange();
            this.dataY = yAxisView.getNumericAxisRange();
            this.evalX = new Dyn.mathUtil.Range( this.dataX );
            this.evalY = new Dyn.mathUtil.Range( this.dataY );
        }
    }

    class FunctionButtons {
        // helper class to lay out the square buttons in the plot for Model Functions
        constructor() {
            this.kNumButtons = 4; // number of buttons in the plot, see makeAllButtons()
            this.button0top = Dyn.plotUtil.mapOrFxButtonTop;
            this.radius = Dyn.plotUtil.mapButtonRadius;
            this.width = Dyn.plotUtil.mapButtonWidth;
            this.height = Dyn.plotUtil.mapButtonWidth;
            this.spacing = Dyn.plotUtil.mapButtonSpacer;
            this.left = Dyn.plotUtil.mapButtonleft;
        }

        top( buttonNum ) {
            return this.button0top + (buttonNum * (this.spacing + this.height))
        }

        bottom( buttonNum ) {
            return this.top( buttonNum ) + this.height;
        }

        marginForButtons() {
            const lastButtonNum = this.kNumButtons - 1,
                yAxisTop = 20;
            return this.bottom( lastButtonNum ) - yAxisTop;
        }
    }

    class FunctionPathSet {
        // helper class to manage the components of the Raphael set that holds the function line and all of its adornments
        constructor( plotElement, buttons ) {
            this.plotElement = plotElement;
            this.kNumPrevAdornments = plotElement.buttons.kNumButtons; // next index after buttons in the adornments array
            this.firstPointIndex = -1;
            this.numPointElements = 0;
        }

        desiredNumberOfAdornments() {
            return this.plotElement.isInPlottableAxes ? (this.kNumPrevAdornments + this.plotElement.formulas.length) : 0;
        }

        getPathSet( adornmentsArray, formulaIndex ) {
            // one set per fx line/curve, contains visible elements on line (e.g. intercept points)
            return adornmentsArray[this.kNumPrevAdornments + (formulaIndex)];
        }

        // add the pathset to the array of adornments, plus the first 4 elements
        addPathElements( adornmentsArray, pathSet, areaElement, pathElement, pathCover, squaresSet ) {
            adornmentsArray.push( pathSet );

            const numElements = pathSet.length;
            pathSet.push( areaElement, pathElement, pathCover, squaresSet );
            pathSet.getAreaEl = function() {
                return this[numElements];
            };
            pathSet.getPathEl = function() {
                return this[numElements+1];
            };
            pathSet.getPathCoverEl = function() {
                return this[numElements+2];
            };
            pathSet.getSquaresSet = function() {
                return this[numElements+3];
            };
            Dyn.assert( pathSet.length === 4 && squaresSet === pathSet.getSquaresSet() );
        }

        // add the label elements to the path set, visible if Show Labels is true
        addLabelElements( pathSet, labelConnectorEl, labelRectElement, labelTextElement ) {
            const numElements = pathSet.length;
            pathSet.push( labelConnectorEl, labelRectElement, labelTextElement );
            pathSet.getLabelConnectorEl = function() {
                return this[numElements];
            };
            pathSet.getLabelRectEl = function() {
                return this[numElements+1];
            };
            pathSet.getLabelTextEl = function() {
                return this[numElements+2];
            };
            this.firstPointIndex = numElements+3; // first point is after the label elements, in every curve
            this.numPointElements = 0;
            pathSet.getPointElement = function( pointIndex ) {
                return this[numElements+3+pointIndex]; // additional elements are points, see addPointEl()
            }
            Dyn.assert( pathSet.getLabelTextEl() === labelTextElement );
        }

        // add a visible point to the pathSet, to be retrieved with getPointElement()
        addPointEl( pathSet, point, pointIndex ) {
            pathSet.push( point.el );
            this.numPointElements++;
            // verify that the point is added and retrieved correctly, see addPathElements() and addLabelElements()
            Dyn.assert( point.el === pathSet.getPointElement( pointIndex )
                && (!!point.el)
                && pathSet.length >= (this.firstPointIndex+this.numPointElements)
                && pointIndex === (this.numPointElements - 1));
        }

        // remove old points in this formula, needed if we now we have fewer roots, etc.
        // to be called after addPointEl() adds all the new points
        removedUnusedPointElements( pathSet, currentPointsArray ) {
            // find the first and last point in the pathSet, beyond the current points
            let i1 = this.firstPointIndex + currentPointsArray.length,
                i2 = i1;
            while( i2 < pathSet.length && pathSet[i2].data( "pointData" ) ) {
                i2++;
            }
            if( i2 > i1 ) { // if there are points to remove
                pathSet.splice( i1, i2 - i1 ).forEach((el) => el.remove());
                this.numPointElements = currentPointsArray.length;
                Dyn.debugging && console.log( "removedUnusedPointElements", "remove:"+(i2 - i1), "keep:"+currentPointsArray.length);
            }
        }

        // create the Raphael set which holds squares on the selected function line
        makeSquaresSet( plotViewPaper ) {
            let squaresSet = plotViewPaper.set();
            // note: don't create function getDashedLine(), unlike refLineElement
            squaresSet.getNthSquare = function( n ) {
                return (n < (this.length)) ? this[n] : null;
            }
            squaresSet.getNumSquares = function() {
                return Math.max( 0, this.length );
            }
            return squaresSet;
        }
    }

    class PlotFunctionElement extends PlotElement {
        /**
         * Dyn.PlotFunctionElement()
         * Create a plot element to hold one or more plottable functions.
         * If no parameters are given, it creates a single default formula
         * @param [formulaID] -- optional formula ID
         * @param [formulaString] -- optional formula string
         * @param [formulaSyntax] -- optional formula syntax, see setFormula() for allowable types
         * @param [constants]
         * @constructor
         */
        constructor( formulaID, formulaString, formulaSyntax, constants, lineColor ) {
            super( Dyn );
            "use strict";
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityPlotFunction,
                replaceType: Dyn.EPlotControl.kControlsPlotFunction,
                primaryType: Dyn.EPlotControl.kControlsPlotFunction,
                otherControls: [
                    Dyn.EPlotControl.kControlsAdornmentPositionOnly,
                    Dyn.EPlotControl.kControlsShowSquares
                ]
            }, Dyn );

            this.formulas = [];             // array of Dyn.PlotFormula objects, usually (but not always) matches the number of Paths
            this.labelOffsets = [];         // array of normalized coordinates for labels positions, one per formula
            this.selectedFormulaIndex = -1; // index into this.formulas of selected line/path or -1 if nothing selected
            this.showSquaresFormulaIndex = -1; // index into this.formulas of the formula with Show Squares checked, or -1 if none
            this.isInPlottableAxes = false; // true if in plot with two numeric axes (e.g. Scatterplot), otherwise false.
            this.plotRange = {};            // numeric range data for overall plot and all evaluated formulas, see initRange()
            this.formulaRanges = [];        // numeric range data for each specific formula, see initRange()
            this.formulaSyntax = 'LaTeX';   // constant
            this.buttons = new FunctionButtons(); // utility class to manage the square buttons
            this.paths = new FunctionPathSet( this ); // utility class to manage the Raphael set that holds the function line and all of its adornments

            if( typeof formulaID === "string" || typeof formulaID === "number" ) {
                this.setFormula( formulaID, formulaString, formulaSyntax, constants, lineColor );
            }
        }

        get kUndoAction() {
            return 'Add Function';
        }

        static debugLogEvalCount = Dyn.debugging && false; // turn on for debugging, 2 console.log lines per function eval
        static debugLogEveryEval = Dyn.debugging && false; // turn on for debugging 1 console.log line per point on curve
        static debugShowPoints = Dyn.debugging && false; // turn on for debugging, show points on curve

        /**
         * Save the PlotElement to a data object that can be JSONified.
         * @returns {{}} - data to be used by restore()
         */
        save( optionalAllFunctionsState ) {
            // NOTE: when save is used for undo/redo of Model Function changes, we allow the Model Function
            // component to pass in the correct state, since it may have changed before being passed on to the plot to save.
            let functionState = optionalAllFunctionsState || Dyn.FunctionUtil.copyAllFunctions();
            Dyn.assert( functionState );
            Dyn.assert( typeof functionState.formulaString === "undefined", "PlotFunctionElement.save(): invalid parameter, use copyAllFunctions() instead of copyFunction()" );
            let plotElementModel = {
                primaryType: this.primaryType,
                plotFunctions: functionState
            };
            if( this.labelOffsets ) {
                plotElementModel.labelOffsets = Dyn.PlotLabelsUtil.copyLabelOffsets2D( this.labelOffsets );
                this.labelOffsets.forEach( (off, i) => {
                    if( off && Number.isFinite( off.connectDataX )) {
                        plotElementModel.labelOffsets[i].connectDataX = off.connectDataX; // save the label connectDataX if it exists
                    }
                });
            }
            if( this.showSquaresFormulaIndex >= 0 ) {
                plotElementModel.showSquaresFormulaIndex = this.showSquaresFormulaIndex;
            }

            return plotElementModel;
        }

        /**
         * Change our PlotElement to match the saved data.
         */
        restore( savedData ) {
            var plotFunctions = this;
            if( savedData && savedData.plotFunctions ) {

                // restore formulas (caller responsible for recalculating plot via Dyn.PlotView)
                this.selectedFormulaIndex = -1;
                this.formulas = []; // erase old
                this.labelOffsets = []; // erase old
                for( var key in savedData.plotFunctions ) {
                    let f = savedData.plotFunctions[key];
                    let constants = Dyn.FunctionUtil.getConstantsFromSliderVars( f );
                    this.setFormula( key, f.formulaString, "LaTeX", constants, f.lineColor, f.lineVisible );
                }
                // restore the label positions
                if( savedData.labelOffsets ) {
                    this.labelOffsets = Dyn.PlotLabelsUtil.copyLabelOffsets2D( savedData.labelOffsets );
                    this.labelOffsets.forEach( (off, i) => {
                        if( off ) {
                            off.connectDataX = savedData.labelOffsets[i]?.connectDataX; // restore the label connectDataX if it exists
                        }
                    });
                }
                this.showSquaresFormulaIndex = (savedData.showSquaresFormulaIndex >= 0 ) ?
                    savedData.showSquaresFormulaIndex : -1;
                // restore formula store and re-populate Model Functions panel if visible
                Dyn.FunctionUtil.restoreAllFunctions( savedData.plotFunctions );
            } else {
                Dyn.FunctionUtil.deleteAllFunctions();
            }
            // WARNING: do nothing after restoreAllFunctions()/deleteAllFunctions()
            // ModelFunctions.restore() is called if panel is open, which updates the plot
        }

        /** Destroy a Dyn.PlotElement */
        destroy() {
            // clear Model Functions'
            // if part of a Reset/PlotView.mixUp(), parent should have called "toolBar:closeModelFunctions"
            // functions should already be saved with this.restore() undo.
            Dyn.FunctionUtil.deleteAllFunctions();

            super.destroy();
            Dyn.assert( this.plotShaper === null, "Dyn.PlotElement.destroy was not called" );
        }

        /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
        calcAxis( ioWhatChanged, subViewRef ) {
            "use strict";
            var xAxisView = subViewRef.xAxisView,
                yAxisView = subViewRef.yAxisView;

            // plot functions only if both axis are numerical
            this.isInPlottableAxes = (xAxisView.isNumberLineAxis() &&
                yAxisView.isNumberLineAxis());
            //  && xAxisView.numeric.axisRange

            if( this.isInPlottableAxes ) { // if buttons visible
                // move Y axis title to the side so it does not overlap with buttons
                yAxisView.setMarginForButtons( this.buttons.marginForButtons() );
            }
        }

        /**
         * Find the nearest point on the function line, in view coordinates.
         * If nearest not found, return an invalid point (.isValid===false)
         * @param otherXY -- point on or off of line
         * @param width -- initial +/- search area in x-axis
         * @param fx -- y=fx(x) function (in view coordinates)
         * @returns {FunctionPointData}
         */
        findNearestPointOnFx( otherXY, fx, lastPointData ) {
            function calcDistanceSquared( p, xy ) {
                // Note: squares of distances can be safely compared, avoids sqrt() calculation
                let dx = p.viewX - xy.x,
                    dy = p.viewY - xy.y;
                return (dx * dx) + (dy * dy);
            }

            function calcFxAndError( viewX, xy ) {
                // find point on curve with save viewX
                let p = fx( viewX ); // get point on curve with .dataX,.dataY, .viewX, .viewY
                if( p.isValid ) {
                    p.error = calcDistanceSquared( p, xy );
                } else {
                    // if point invalid, return large error so point is not nearest
                    p.error = Dyn.mathUtil.MAX_SAFE_INTEGER;
                }
                return p;
            }

            function linearSearchForValidPt( pGood, pBad ) {
                // find a valid point between pGood and pBad fall back to pGood as worst case
                let x1 = pGood.viewX,
                    x2 = pBad.viewX,
                    pLastGood = pGood,
                    pNext = pGood,
                    step = (x2 - x1) / 10; // step may be positive or negative
                Dyn.assert( pGood.isValid );
                for( let xi = x1 + step; (((step > 0) ? (xi < x2) : (xi > x2)) && pNext.isValid); xi += step ) {
                    pNext = calcFxAndError( xi, otherXY );
                    if( pNext.isValid ) {
                        pLastGood = pNext;
                    }
                    //console.log( "linearSearchForValidPt", xi, pNext.isValid );
                }
                Dyn.assert( pLastGood.isValid );
                return pLastGood;
            }

            function findTwoValidPoints( p0, p1, p2 ) {
                // if any points invalid, do linear search to try to find 2 good points
                if( p0.isValid ) {
                    if( p1.isValid && !p2.isValid ) {
                        p2 = linearSearchForValidPt( p0, p2 );
                    } else if( p2.isValid && !p1.isValid ) {
                        p1 = linearSearchForValidPt( p0, p1 );
                    }
                } else {
                    if( p1.isValid && !p2.isValid ) {
                        p2 = linearSearchForValidPt( p1, p0 );
                    } else if( p2.isValid && !p1.isValid ) {
                        p1 = linearSearchForValidPt( p2, p0 );
                    }
                }
            }

            // Uses stepwise approximation to find the nearest point on curve.
            //      Search area is p0 +/- width.
            //      p0 = [ fx(otherXY.x), otherXY.x ]
            //      width = 2*(distance( p0, otherXY )
            // Alternative algorithm #1 would measure the length of the perpendicular of the line segments of the curve.
            // Alternative algorithm #2 would be to have direct formula for perpendicular values of curve.
            const targetError = 1; // loop until our measured width is within x pixels of actual closest point
            const usePerpendicularInX = true;

            let p0 = calcFxAndError( otherXY.x, otherXY ), // point on line with same x as otherXY.x
                width = Math.sqrt( p0.error ), // initial search area, if p0 valid
                pLast = lastPointData.wantVisible ? calcFxAndError( lastPointData.viewX, otherXY ) : null;
            if( usePerpendicularInX ) {
                return p0; // skip the fancy stuff, just get x value on curve.
            }
            if( pLast && (pLast.error < p0.error) ) {
                width = Math.sqrt( pLast.error ); // initial search area, if pLast valid and closer
            }
            let p1 = calcFxAndError( otherXY.x - width, otherXY ),
                p2 = calcFxAndError( otherXY.x + width, otherXY );
            findTwoValidPoints( p0, p1, p2 );
            // now do search between p1 and p2,
            //      step either p1 or p2, 1/2 of distance each time,
            //      until width within target
            while( width > targetError && (p1.isValid || p2.isValid) ) {
                // step either p1 or p2 in, which ever is farther from otherXY
                if( p1.error > p2.error ) {
                    p1 = calcFxAndError( p2.viewX - width, otherXY ); // move p1.x up 1/2 way to p2
                } else {
                    p2 = calcFxAndError( p1.viewX + width, otherXY ); // move p2.x down 1/2 way to p1
                }
                width /= 2; // next time half the dist, so p1 < p2 always
            }
            if( p1.isValid && p2.isValid ) {
                return fx( (p1.viewX + p2.viewX) / 2 ); // mid-point of p1 and p2
            }
            // one or more invalid points because fx() returns invalid points near otherXY; try to return a valid one if it exists.
            return (p1.isValid ? p1 : (p2.isValid ? p2 : p0));
        }

        /**
         * Update adornments (specific to this element) to show a math-function curve in the plot.
         * The maximum number of regression lines this element controls is:
         *      (number of cells * number of numeric attributes on one axis * number of categories in legend )
         */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            let adornsArray = ioAdornArray,
                plotViewPaper = this.plotShaper.adornments.paper,
                plotElement = this, // class PlotFunctionElement
                paths = this.paths, // class FunctionPathSet
                isUpdateOnly = false;

            // clear any old adornments from previous PlotFunctionElements
            if( adornsArray.length === paths.desiredNumberOfAdornments() ) {     // ASSUMES one path adornment per formula when complete, this may change.
                isUpdateOnly = true;
            } else {
                this.removeGlow(); // remove the glow in case the curve is deleted; we will add the glow back in later if the curve is re-created.
                Dyn.plotUtil.removeAndEmptyArrayOfElements( adornsArray );
                let measuresArray = this.plotShaper.measures.getMeasuresArray( this.primaryType );
                measuresArray.length = 0; // remove old measures (values for summary table)
            }

            this.makeAllButtons( adornsArray, this.isInPlottableAxes && !isUpdateOnly );

            function getPointElement( adornmentArray, formulaIndex, pointIndex ) {
                // get the visible or hidden point element on the fx line/curve; null or undefined if not found
                return paths.getPathSet( adornmentArray, formulaIndex )?.getPointElement( pointIndex );
            }

            function pathHoverInOrMove( event ) { // also handles mousemove to update tooltip
                event.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                var pathCover = this,
                    targetIsPath = event.target.raphaelid == pathCover.id, // else hover/mousemove over label rect, not path cover
                    isInDrag = pathCover.data( "isInDrag" ),
                    pathElement = this.data( "pathElement" ),
                    pathColor = pathCover.data( "pathColor" ),
                    formulaIndex = pathCover.data( "formulaIndex" ),
                    paperXY = Dyn.plotUtil.hoverEventToPaperXY( event, pathCover ),
                    hoverPointElement = getPointElement( adornsArray, formulaIndex, kHoverPointIndex ),
                    lastPointData = hoverPointElement.data( "pointData" ),
                    hoverPointData = getPointOnPath( pathCover, event, paperXY, kHoverPointIndex, lastPointData );
                if( hoverPointData.isChangedPoint( hoverPointElement ) ) {
                    hoverPointData.setElement( hoverPointElement ); // new hover point location
                    if( targetIsPath && hoverPointData.isValid && !isInDrag ) {
                        hoverPointElement.attr( { cx: hoverPointData.viewX, cy: hoverPointData.viewY } );
                        hoverPointData.show();
                    } else {
                        hoverPointData.hide();
                    }
                }
                plotElement.addGlow( pathElement, pathColor, Dyn.plotUtil.functionGlowWidth );
            }

            function pathHoverOut( /* event  */ ) {
                var pathCover = this,
                    isInDrag = pathCover.data( "isInDrag" ),
                    formulaIndex = pathCover.data( "formulaIndex" ),
                    hoverPointElement = getPointElement( adornsArray, formulaIndex, kHoverPointIndex ),
                    hoverPointData = hoverPointElement.data( "pointData" );
                hoverPointData.hide();
                if( !isInDrag ) {
                    plotElement.removeGlow();
                }
            }

            function pathDragStart( x, y, event ) {
                let pathCover = this;
                pathCover.data( "isInDrag", true );
                pathClickOrDrag( pathCover, event, true );
            }

            function pathDragMove( dx, dy, x, y, event ) {
                pathClickOrDrag( this, event, false );
            }

            function pathDragEnd() {
                let pathCover = this;
                pathCover.data( "isInDrag", false );
                plotElement.removeGlow();
                if( Dyn.toolTip.isMyElement( pathCover ) ) {
                    Dyn.toolTip.hide();
                }
            }

            function pathClickOrDrag( pathCover, event, isStart ) {
                var formulaIndex = pathCover.data( "formulaIndex" ),
                    paperXY = Dyn.plotUtil.hoverEventToPaperXY( event, pathCover ),
                    clickPointElement = getPointElement( adornsArray, formulaIndex, kClickPointIndex ),
                    hoverPointElement = getPointElement( adornsArray, formulaIndex, kHoverPointIndex ),
                    hoverPointData = hoverPointElement.data( "pointData" ),
                    lastPointData = isStart ? hoverPointData : clickPointElement.data( "pointData" ),
                    clickPointData = getPointOnPath( pathCover, event, paperXY, kClickPointIndex, lastPointData );
                if( clickPointData.isChangedPoint( clickPointElement ) ) {
                    clickPointData.setElement( clickPointElement ); // new click point location
                    if( clickPointData.isValid ) {
                        hoverPointData.hide();
                        clickPointElement.attr( { cx: clickPointData.viewX, cy: clickPointData.viewY } );
                        clickPointData.show();
                    } else {
                        clickPointData.hide();
                    }
                }
                if( isStart ) {
                    plotElement.selectAdornment( formulaIndex );
                    Dyn.mediator.publish( "plot:updateAdornment", { adornmentSelected:true } ); // to show/hide squares
                }
                showToolTipText( pathCover, event, clickPointData );
            }

            function pointHoverIn( event ) {
                var pointElement = this,
                    hoverPointData = this.data( "pointData" ),
                    pathCover = pointElement.data( "pathCover" ),
                    pathElement = pathCover.data( "pathElement" ),
                    pathColor = pathCover.data( "pathColor" );
                if( !hoverPointData.display ) { // if not already calculated by getPointOnPath()
                    hoverPointData.updateDisplay(
                        xAxisView.getAttribute(), yAxisView.getAttribute(),
                        getPointPrecisionX(), getPointPrecisionY() );
                }
                plotElement.addGlow( pathElement, pathColor, Dyn.plotUtil.functionGlowWidth );
                showToolTipText( pathCover, event, hoverPointData );
            }

            function pointHoverOut( /* event  */ ) {
                var pointElement = this,
                    pathCover = pointElement.data( "pathCover" ),
                    isInDrag = pathCover.data( "isInDrag" );
                if( Dyn.toolTip.isMyElement( pathCover ) && !isInDrag ) {
                    Dyn.toolTip.hide();
                }
                plotElement.removeGlow();
            }

            function getPointOnPath( coverElement, event, paperXY, pointIndex, lastPointData ) {
                function viewXtoPointOnPath( newViewX ) {
                    let newDataX = xAxisView.coordMap.inverseTransform( newViewX ),
                        pt = new FunctionPointData( newDataX,
                            evaluateAtX( newDataX, formulaObj, formulaScope ),
                            pointIndex );
                    pt.updateViewCoords( xAxisView, yAxisView );
                    pt.viewX = newViewX;
                    return pt;
                }

                let formulaIndex = coverElement.data( "formulaIndex" ),
                    formulaScope = coverElement.data( "formulaScope" ),
                    formulaObj = plotElement.formulas[formulaIndex],
                    xAttr = xAxisView.getAttribute(),
                    yAttr = yAxisView.getAttribute(),
                    pointData = new FunctionPointData( 0, 0, pointIndex );

                let xPrecision, yPrecision; // default to attribute precision if invalid point
                if( formulaObj.canEvaluate() ) { // don't evaluate if formula restored but not yet parsed
                    pointData = plotElement.findNearestPointOnFx( paperXY, viewXtoPointOnPath, lastPointData );
                    if( pointData.isValid ) {
                        let xOpts = {};
                        // get x,y at the right precision
                        pointData.dataX = xAxisView.snapIfCloseToTickmark( pointData.dataX, Dyn.plotUtil.functionSnapDist );
                        pointData.dataX = Dyn.mathUtil.roundToScale( pointData.dataX, getAxisScale( xAxisView ), xOpts );
                        pointData.dataY = evaluateAtX( pointData.dataX, formulaObj, formulaScope );
                        pointData.updateViewCoords( xAxisView, yAxisView );
                        // get number of decimal places for formatting numbers
                        xPrecision = xOpts.precision;
                        yPrecision = getPointPrecisionY();
                    }
                }
                pointData.updateDisplay( xAttr, yAttr, xPrecision, yPrecision );
                return pointData; // caller should check pointData.isValid
            }

            function getAxisScale( axisView ) {
                const extraPrecision = 10; // one more decimal place to ensure enough precision even with rounding
                return Math.abs( Dyn.mathUtil.invertSafely( axisView.coordMap.scale * extraPrecision ) );
            }

            function getPointPrecisionX() {
                // get precision to use for any arbitrary point on curve, under mouse, for axisValueToString()
                // note: must be synchronized with getPointOnPath()
                let xPixelScale = getAxisScale( xAxisView ),
                    xOpts = {};
                Dyn.mathUtil.roundToScale( 0, xPixelScale, xOpts ); // get precision for yPixelScale
                return xOpts.precision;
            }

            function getPointPrecisionY() {
                // get precision to use for any arbitrary point on curve, under mouse, for axisValueToString()
                let yPixelScale = getAxisScale( yAxisView ),
                    yAttr = yAxisView.getAttribute(),
                    yOpts = {};
                Dyn.mathUtil.roundToScale( 0, yPixelScale, yOpts ); // get precision for yPixelScale
                return Math.max( yOpts.precision, yAttr.getNumericDecimals() ) + Dyn.preferences.getPrecision();
            }

            function showToolTipText( coverElement, event, pointData ) {
                let newTip = Dyn.loc( "({{x}}, {{y}})", pointData.display ),
                    oldTip = coverElement.data( "tooltip" );
                coverElement.data( "tooltip", newTip );
                if( !pointData.isValid ) {
                    Dyn.toolTip.hide();
                } else if( Dyn.toolTip.isMyElement( coverElement ) ) {
                    // reduce updates while dragging; only update tip if significant text change
                    if( newTip !== oldTip ) {
                        Dyn.toolTip.update( coverElement, event, { x: pointData.viewX, y: pointData.viewY } );
                    }
                } else {
                    Dyn.toolTip.show( coverElement, event, { x: pointData.viewX, y: pointData.viewY } );
                }
            }

            function evaluateAtX( x, formulaObj, scope ) {
                formulaObj.getPlotFunctionScope( scope, x ); // define attributes and "x" at current x value
                return formulaObj.evaluate( scope, 'number' ); // evaluate and return an error if result is not type 'number'
            }

            function getPointData( formulaIndex, pointIndex, optionalNewDataX ) {
                // get or create the point data at the given index
                let e = getPointElement( adornsArray, formulaIndex, pointIndex ),
                    p = (e ? e.data( "pointData" ) : new FunctionPointData( 0, 0, pointIndex ));
                Dyn.assert( p instanceof FunctionPointData );
                if( p.isIntercept && Dyn.mathUtil.isFinite( optionalNewDataX ) ) {
                    p.dataX = optionalNewDataX; // update dataX for intercepts
                }
                Dyn.assert( p.isElementValidOrNull() );
                return p;
            }

            function addPointToCurve( paths, pathSet, point, pointIndex ) {
                // create a visible point element from the point data, and add to the function line's set of elements.
                let circleEl = plotViewPaper.circle( point.viewX, point.viewY, Dyn.plotUtil.functionPointRadius ),
                    pathCover = pathSet.getPathCoverEl();
                if( !Dyn.environment.isTouchDevice() ) {
                    if( point.isHoverPoint ) {
                        Dyn.plotUtil.disableMouseOverEvents( circleEl );
                    } else {
                        circleEl.hover( pointHoverIn, pointHoverOut )
                            .drag( pathDragMove.bind( pathCover ), pathDragStart.bind( pathCover ), pathDragEnd.bind( pathCover ) );
                    }
                }
                // add reference to pathCover and point data for managing the point
                point.setElement( circleEl );
                paths.addPointEl( pathSet, point, pointIndex );
            }

            this.initRange( xAxisView, yAxisView );
            if( !this.isInPlottableAxes ) {
                return;
            }

            let rangeX = xAxisView.getNumericAxisRange(),
                viewRangeX = new Dyn.mathUtil.Range( xAxisView.bounds.left(), xAxisView.bounds.right() ),
                clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
                dataSet = this.plotShaper.dataSet;

            this.formulas.forEach( ( formulaObj, formulaIndex ) => {
                if( this.isSingleFunctionUpdate && (this.singleFunctionUpdateID !== formulaObj.id)) {
                    return;
                }
                let scope = formulaObj.findDependencies( dataSet ), // parse formula if changed
                    roots = formulaObj.rootsAndInterceptsOfFx( scope, rangeX ); // find finite numeric roots if any
                let canEvaluate = formulaObj.canEvaluate() && formulaObj.isLineVisible,
                    formulaRange = this.formulaRanges[formulaIndex],
                    pathSet = paths.getPathSet( adornsArray, formulaIndex ),
                    areaElement = pathSet?.getAreaEl(), // path elements in order determined by addPathElements()
                    pathElement = pathSet?.getPathEl(),
                    pathCover = pathSet?.getPathCoverEl(),
                    squaresSet = pathSet?.getSquaresSet(),
                    labelConnectorEl = pathSet?.getLabelConnectorEl(), // label elements in order determined by addLabelElements()
                    labelRectElement = pathSet?.getLabelRectEl(),
                    labelTextElement = pathSet?.getLabelTextEl(),
                    points = [ // last hover and click point always exist, but may be hidden
                        getPointData( formulaIndex, kHoverPointIndex ), // hoverPointElement
                        getPointData( formulaIndex, kClickPointIndex ), // clickPointElement
                    ],
                    pathString = "", areaString = "",
                    pathColor = formulaObj.lineColor;
                // add points for roots and axis intercepts
                roots.intercepts.forEach( x => points.push( getPointData( formulaIndex, points.length, x ) ) );
                if( canEvaluate ) {
                    // get the path string for this f(x) curve, and update this.plotRange and formulaRange
                    const calcPointOnCurve = ( pointData, dataX ) => {
                        pointData.dataX = dataX;
                        pointData.dataY = evaluateAtX( dataX, formulaObj, scope );
                        pointData.updateViewCoords( xAxisView, yAxisView );
                    }
                    pathString = this.calcPathString(
                        rangeX, viewRangeX, xAxisView, this.plotRange, formulaRange, formulaObj, calcPointOnCurve );
                    areaString = this.calcInequalityPathString(
                        pathString, rangeX, viewRangeX, xAxisView, formulaRange, formulaObj, calcPointOnCurve );
                    formulaObj.evalCountStart();
                    points.forEach( p => {
                        p.dataY = evaluateAtX( p.dataX, formulaObj, scope );
                        p.updateViewCoords( xAxisView, yAxisView );
                        if( p.isIntercept && !p.display ) { // update intercept display values for Summary View
                            p.updateDisplay( xAxisView.getAttribute(), yAxisView.getAttribute(),
                                getPointPrecisionX(), getPointPrecisionY() );
                        }
                    } );
                    if( PlotFunctionElement.debugLogEvalCount ) {
                        console.log( "fx evaluations", formulaObj.evalCount(), "for points on function", formulaObj.nerdamerString );
                    }
                }
                if( pathString.length < 1 ) {
                    // there were no plotable points, create an empty path as a placeholder for this function
                    pathString = "M0,0";
                }
                if( areaString.length < 1 ) {
                    areaString = "M0,0"; // there is no inequality area for this function
                }
                let animationTime = 0;
                const animationEasing = Dyn.plotUtil.adornAnimationEasing;
                if( isUpdateOnly ) {
                    // move path to new position with animation
                    animationTime = (noAnimation ? 0 : (this.isUpdateToConstantsOnly ? Dyn.plotUtil.functionFastAnimationTimeMs : Dyn.plotUtil.adornAnimationTimeMs));
                    areaElement.animate( { path: areaString }, animationTime, Dyn.plotUtil.adornAnimationEasing );
                    pathElement.animate( { path: pathString }, animationTime, Dyn.plotUtil.adornAnimationEasing );
                    pathCover.animate( { path: pathString }, animationTime, Dyn.plotUtil.adornAnimationEasing );
                    points.forEach( ( p, pi ) => {
                        if( p.el ) {  // update existing point
                            p.el.animate( {
                                cx: p.viewX,
                                cy: p.viewY
                            }, animationTime, animationEasing );
                        } else {      // line is new but point is added (e.g. extra root because of formula change)
                            addPointToCurve( paths, pathSet, p, pi );
                        }
                    } );
                    // remove old points in this formula, if now we have fewer roots, etc.
                    paths.removedUnusedPointElements( pathSet, points );
                } else {
                    // create one set of elements for this function, no animation
                    pathSet = plotViewPaper.set();
                    areaElement = plotViewPaper.path( areaString ).attr({ stroke:"none" });
                    Dyn.plotUtil.disableMouseOverEvents( areaElement );
                    pathElement = plotViewPaper.path( pathString );
                    pathCover = plotViewPaper.path( pathString )
                        .drag( pathDragMove, pathDragStart, pathDragEnd );
                    squaresSet = this.paths.makeSquaresSet( plotViewPaper );

                    // add label elements to the set
                    [labelConnectorEl, labelRectElement, labelTextElement]
                        = this.initLabelElements( pathSet, formulaIndex);

                    if( !Dyn.environment.isTouchDevice() ) {
                        pathCover.hover( pathHoverInOrMove, pathHoverOut )
                            .mousemove( pathHoverInOrMove );
                        labelRectElement.hover( pathHoverInOrMove.bind( pathCover ), pathHoverOut.bind( pathCover ))
                            .mousemove( pathHoverInOrMove.bind( pathCover ) );
                    }

                    // add the set of path elements
                    paths.addPathElements( adornsArray, pathSet, areaElement, pathElement, pathCover, squaresSet );
                    paths.addLabelElements( pathSet, labelConnectorEl, labelRectElement, labelTextElement );
                    points.forEach( ( p, pi ) => {
                        addPointToCurve( paths, pathSet, p, pi );
                    } );
                }

                // adornment updates common to new or updated functions
                const inequalityAreaOpacity = 0.2;
                const inequalityDashArray = '10,10';
                areaElement.attr({ // note: path is null when not an inequality
                    fill:pathColor,
                    'fill-opacity': inequalityAreaOpacity,
                    'clip-rect': clipRectString } ); // clip to plot interior
                pathElement.attr( {
                    stroke: pathColor,
                    "stroke-width": Dyn.plotUtil.functionCurveWidth,
                    "stroke-linecap": "round",
                    'clip-rect': clipRectString } ); // clip to plot interior
                pathElement.node.setAttribute( 'stroke-dasharray', formulaObj.isDashedInequality() ? inequalityDashArray : null ); // long dashes for >= and <=
                let coverWidth = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.functionCoverWidth ),
                    isSelectedFormula = (formulaIndex === this.selectedFormulaIndex);
                pathCover   // transparent cover with wider target area
                    .attr( Dyn.debugViewRects ?
                        { 'clip-rect': clipRectString, "stroke-width": coverWidth, stroke: "orange", opacity: 0.5 } :
                        {
                            'clip-rect': clipRectString,
                            "stroke-width": coverWidth,
                            stroke: Dyn.plotUtil.transparentColor
                        } )
                    .data( "pathColor", pathColor )
                    .data( "pathElement", pathElement )
                    .data( "formulaScope", scope )
                    .data( "formulaIndex", formulaIndex );

                // update labels to the curve
                const isShowingLabels = this.plotShaper.showLabelsOnStats && canEvaluate && formulaRange.isFinite();
                if( isShowingLabels ) {
                    this.updateLabelElements( pathSet, formulaObj, formulaIndex, formulaRange, scope,
                        xAxisView, yAxisView, animationTime, animationEasing );
                }
                Dyn.plotUtil.showOrHideElement( labelConnectorEl, isShowingLabels );
                Dyn.plotUtil.showOrHideElement( labelRectElement, isShowingLabels );
                Dyn.plotUtil.showOrHideElement( labelTextElement, isShowingLabels );

                let showSumOfSquares = this.plotShaper.showSquaresOnLines,
                    showSquares = this.plotShaper.showSquaresOnLines && (this.showSquaresFormulaIndex === formulaIndex) && canEvaluate,
                    squaresData = {
                        fx: ( x ) => { formulaObj.getPlotFunctionScope( scope, x );
                            return formulaObj.evaluate( scope, 'number' ); },
                        filter: Dyn.SquaresOnLineModel.xyNumericFilter() };
                this.updateShowSquares( showSquares, squaresData, xAxisView, yAxisView, squaresSet, pathElement, pathColor );
                this.updateSumOfSquares( showSumOfSquares, squaresData, xAxisView, yAxisView  );

                points.forEach( p => {
                    p.el.data( "pathCover", pathCover ) // note: has extra data referenced by point
                        .attr( {
                            stroke: pathColor,
                            fill: (p.isIntercept ? pathColor : "none") /*Dyn.plotUtil.functionPointColor*/,
                            'clip-rect': clipRectString
                        } );
                    p.updateVisibility( isSelectedFormula && canEvaluate );
                } );

                formulaRange.formatRange( xAxisView, yAxisView );
                mediator.publish( "plot:plotFunctionMessage", plotElement.getFormulaMessage( formulaIndex )); // notify Model Functions panel
                this.addToMeasures( formulaIndex, xAxisView, yAxisView, formulaObj, scope, points, roots.sampled, squaresData.sumOfSquares );
            }, this );
            this.isUpdateToConstantsOnly = false;           // clear flag set by last update of this.setFormula();
            this.isSingleFunctionUpdate = false;            // clear flag set by last update of this.setFormula();
            Dyn.assert( adornsArray.length === paths.desiredNumberOfAdornments(), "mismatch in number of plotFunction adornments" );
        }

// Dyn.PlotFunctionElement.prototype.resetMeasures = function() {
//     let plotMeasures = this.plotShaper.measures,
//         measuresArray = plotMeasures.getMeasuresArray( this.primaryType );
//     measuresArray.length = 0; // remove old measures (values for summary table)
// };

        /** add a measure value for our plot summary table */
        addToMeasures( formulaIndex, xAxisView, yAxisView, formulaObj, scope, points, isSampleOfPoints, sumOfSquares ) {
            let plotMeasures = this.plotShaper.measures,
                measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
                xAttr = xAxisView.getAttribute(),
                yAttr = yAxisView.getAttribute(),
                formulaID = formulaObj.id,
                modelNumber = formulaIndex + 1,
                oldMeasureIndex = measuresArray.findIndex( m => m.modelNumber === modelNumber );

            // create "m=20.3, b=0.132" from scope={ m:20.3, x=-1, b=0.121999999999999 }
            let constantsString = "";
            for( let c in scope ) {
                if( scope.hasOwnProperty( c ) ) {
                    if( c !== "x" ) { // ignoring special 'x' parameter
                        let spacer = (constantsString.length > 0) ? ', ' : '',
                            value = Dyn.mathUtil.roundToDecimalPlaces( scope[c], Dyn.mathUtil.kPrecision );
                        constantsString += (spacer + c + "=" + value);
                    }
                }
            }

            // create points string for intercepts and click point (if any)
            let pointsString = "";
            points.forEach( p => {
                if( p.isValid && p.isIntercept && p.display ) {
                    let spacer = (pointsString.length > 0) ? ' ' : '',
                        x = p.display.x, y = p.display.y;
                    pointsString += (spacer + Dyn.loc( "({{x}},{{y}})", { x: x, y: y } ));
                }
            } );
            if( isSampleOfPoints ) {
                pointsString += " ...";
            }

            /**
             * Class that holds a model function measure.
             * Each measure is a value in one of the Summary View tables, along with the metadata necessary to
             * determine which table, row, and column this value should appear in.
             */
            class modelFunctionMeasure {
                constructor( modelNumber, name, value ) {
                    this.setID = "modelFunction"; // which table
                    this.modelNumber = modelNumber; // which row
                    this.measureName = name; // which column
                    this.value = value; // cell value
                }
            }

            // add these 5 measures as a row of the model function table in the summary tables
            let newMeasures = [];
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "y" ), (yAttr ? yAttr.name : '') ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, plotMeasures.getNameOfFormulaMeasure(), formulaObj.latexString ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "x" ), (xAttr ? xAttr.name : '') ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "Constants" ), constantsString ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "Intercepts" ), pointsString ) );
            if( sumOfSquares?.length > 0 ) {
                newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "Sum of Squares" ), sumOfSquares ) );
            }
            newMeasures.push( new modelFunctionMeasure( modelNumber, plotMeasures.getNameOfColorMeasure(), formulaObj.lineColor ) );
            plotMeasures.addToOrUpdateMeasuresArray( measuresArray, newMeasures, oldMeasureIndex )
        }

        // view coordinate (x or y) to string, clipping extra precision for a shorter path string
        _toPathString( op, x, y ) {
            // to avoid path string parsing errors by Raphael/SVG, clip very small or large values to these points, which should be offscreen anyway
            const minViewCoord = -1e6, maxViewCoord = 1e6;
            Dyn.assert( Number.isFinite(x) && Number.isFinite(y));
            return op + Dyn.mathUtil.clipToMinMax( x.toFixed( 1 ), minViewCoord, maxViewCoord )
                + ',' + Dyn.mathUtil.clipToMinMax( y.toFixed( 1 ), minViewCoord, maxViewCoord );
        }

        /**
         * Calculate the path string for a single function curve
         * @param dataRangeX - the range of data values for the x-axis
         * @param viewRangeX - the range of view values for the x-axis (view coordinates)
         * @param xAxisView {Dyn.AxisView} - the x-axis view
         * @param plotRange - the range of X,Y data values visible in the plot
         * @param formulaRange - the range of X,Y data values for this formula
         * @param formulaObj - the formula object for this curve
         * @param calcPointOnCurve {formula} - a function that calculates a point on the curve
         * @returns {string} - path string for the curve
         */
        calcPathString( dataRangeX, viewRangeX, xAxisView, plotRange, formulaRange, formulaObj, calcPointOnCurve ) {
            let pathString = "",
                plotElement = this;

            // Note: all angles are in Radians
            function calcPointAndDeltaSlopeInView( newDeltaViewX ) {
                deltaViewX = newDeltaViewX;
                deltaDataX = xAxisView.coordMap.inverseScaleAbs( deltaViewX );
                deltaAngle = Math.NaN;
                calcPointOnCurve( pt, lastPt.dataX + deltaDataX );
                if( pt.isValid && lastPt.isValid ) {
                    // slope Angle in Radians in View coordinates (ARV), e.g. 0 radians for horizontal line
                    pt.slopeARV = Math.atan2( lastPt.viewY - pt.viewY, pt.viewX - lastPt.viewX );
                    if( Number.isFinite( lastPt.slopeARV ) ) {
                        deltaAngle = Math.abs( pt.slopeARV - lastPt.slopeARV );
                        if( deltaViewX > 1 ) { // adjust angle for long runs
                            deltaAngle *= deltaViewX;
                        }
                    }
                }
            }

            function debugLogCalculation( pt, evalCount, str ) {
                if( PlotFunctionElement.debugLogEveryEval ) {
                    console.log( "fx pt", ptCount, "x,y:", pt.dataX, pt.dataY, '+vx:', deltaViewX,
                        'deltaAngle=' + (Number.isFinite( deltaAngle ) ? deltaAngle.toFixed( 5 ) : deltaAngle),
                        "try " + evalCount + " " + str );
                }
            }

            formulaObj.evalCountStart();
            let hasDiscontinuity = false,   // path should be invisible where not continuous
                deltaViewX = Dyn.plotUtil.fxInitialDeltaViewX,
                deltaDataX = xAxisView.coordMap.inverseScaleAbs( deltaViewX ),
                deltaAngle,
                lastPt = new FunctionPointData( dataRangeX.min - deltaDataX ),
                pt = new FunctionPointData( lastPt.dataX ),
                ptCount = 0;
            calcPointOnCurve( lastPt, lastPt.dataX );
            // evaluate for points along X axis, spacing of points will vary according to slope
            do {
                let evalCount = 0;
                calcPointAndDeltaSlopeInView( deltaViewX );
                // debugLogCalculation( pt, ++evalCount, "" );
                if( pt.isValid ) {
                    // if needed, move point to expand distance between points, so curve > minAngle
                    //  but not more than maxDeltaViewX points from the last point, for efficiency
                    while( deltaAngle < Dyn.plotUtil.fxMinAngle && deltaViewX < 0.5 * Dyn.plotUtil.fxMaxDeltaViewX ) {
                        calcPointAndDeltaSlopeInView( deltaViewX * 2 );
                        // debugLogCalculation( pt, ++evalCount, (deltaAngle >= Dyn.plotUtil.fxMinAngle ? "angle>=min " : "") + (deltaViewX >= 0.5 * Dyn.plotUtil.fxMaxDeltaViewX ? "dx>max/2" : "") );
                    }
                    // if needed, move point to shrink distance between points, so curve < maxAngle
                    //  but not more than maxDeltaViewX points from the last point, for efficiency
                    while( deltaAngle > Dyn.plotUtil.fxMaxAngle && deltaViewX > Dyn.plotUtil.fxMinDeltaViewX * 2 ) {
                        calcPointAndDeltaSlopeInView( deltaViewX / 2 );
                        // debugLogCalculation( pt, ++evalCount, (deltaAngle <= Dyn.plotUtil.fxMaxAngle ? "angle<=max " : "") + (deltaViewX <= 2 * Dyn.plotUtil.fxMinDeltaViewX ? "dx>min*2 " : "")
                        //     + (deltaAngle > Dyn.plotUtil.fxDiscontinuityAngle ? "+hasDiscontinuity because of angle" : "") );
                    }
                    if( deltaAngle > Dyn.plotUtil.fxDiscontinuityAngle ) {
                        hasDiscontinuity = true;
                    }
                    // add point to path
                    let debugShowAsDash = PlotFunctionElement.debugShowPoints && Dyn.mathUtil.isEven(ptCount), // show individual points as dash, if debugging
                        op = ((pathString.length === 0 || hasDiscontinuity || debugShowAsDash) ? "M" : "L");
                    pathString += this._toPathString( op, pt.viewX, pt.viewY );
                    // track the points: expand plotRange and formulaRange
                    plotRange.evalX.addPoint( pt.dataX );
                    plotRange.evalY.addPoint( pt.dataY );
                    formulaRange.addToRange( pt.dataX, pt.dataY, hasDiscontinuity, pt.viewX, pt.viewY );
                    hasDiscontinuity = false;
                } else {
                    formulaRange.addUndefinedPoint();
                    hasDiscontinuity = true;
                }
                // increment point
                ++ptCount;
                lastPt.copyData( pt );
                lastPt.slopeARV = pt.slopeARV;
            } while( ptCount < 2 || pt.dataX < dataRangeX.max );

            if( PlotFunctionElement.debugLogEvalCount ) {
                console.log( "fx evaluations", formulaObj.evalCount(), "for", ptCount, "points in curve, width", Math.round( viewRangeX.width() ), "function", formulaObj.nerdamerString );
            }
            return pathString;
        }

        /** calculate the path string for the inequality area of a function curve, if any */
        calcInequalityPathString( linePath, dataRangeX, viewRangeX, xAxisView, formulaRange, formulaObj, calcPointOnCurve ) {
            let areaString = "";

            // check for inequality, a valid line path, and points to define the area
            if( !formulaObj.isInequality || !linePath || !formulaRange.evalX ) {
                return areaString; // no inequality in this formula -> return empty area path
            }

            // create the path string for the area, dividing the line path into segments
            // each segment is a polyline "M...L..." has points added to make a polygon "M...L...Z"
            // that is filled to the top or bottom of the plot
            let yTop = 0,
                yBottom = xAxisView.paper.height,
                segments = linePath.split( 'M' ).filter( p => /L/.test( p ) ); // split and remove single-point segments
            segments.forEach( ( segment, i ) => {
                let points = segment.split( 'L' ), // split into '123.3,456.7' x, y segments
                    xFirst = Number( points.at( 0 ).split( ',' )[0]),
                    xLast = Number( points.at( -1 ).split( ',' )[0]),
                    str = 'M' + segment; // re-build the path segment
                if( formulaObj.isInequalityGtOrGe() ) {
                    str += this._toPathString( 'L', xLast, yTop ); // from last point up to top of visible plot
                    str += this._toPathString( 'L', xFirst, yTop ); // over to top of first visible point
                } else {
                    str += this._toPathString( 'L', xLast, yBottom ); // from last point down to bottom of visible plot
                    str += this._toPathString( 'L', xFirst, yBottom ); // over to bottom of first visible point
                }
                str += "Z"; // close path to connect to first point
                areaString += str; // one or more segments
            } );

            return areaString; // zero or more segments, usually one if an inequality, two if discontinuous at x=0, etc
        }

        /** make the buttons for the function curves: zoom in, zoom out, view to data, view to all of curve */
        makeAllButtons( adornsArray, isFirstPlot ) {
            if( isFirstPlot ) {
                let buttonNum = 0,
                    buttonSize = Dyn.plotUtil.iconSize; // DON'T scale for touch devices, it is big enough
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "axisPlus", Dyn.loc( "zoom in" ), Raphael.transformPath( Dyn.plotUtil.getPlusIcon( buttonSize ), "T17,15" ) ) );
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "axisMinus", Dyn.loc( "zoom out" ), Raphael.transformPath( Dyn.plotUtil.getMinusIcon( buttonSize ), "T17,15" ) ) );
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "resetToData", Dyn.loc( "reset view" ), Raphael.transformPath( Dyn.plotUtil.iconResetView, "T-9,-9s0.4,0.4" ) ) );
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "resetToEval", Dyn.loc( "show all of curve on y axis" ), Raphael.transformPath( Dyn.plotUtil.getFitEquationIcon(), "T7,6" ) ) );
                // TODO: make a context menu button
                //adornsArray.push( this.makeContextMenuButton( adornsArray, buttonNum++ )); // context menu button
                Dyn.assert( this.buttons.kNumButtons === buttonNum, "number of buttons mismatch" );
            }
            this.buttonPlus = adornsArray[0] || null;
            this.buttonMinus = adornsArray[1] || null;
            this.buttonReset = adornsArray[2] || null;
            this.buttonFit = adornsArray[3] || null;
        }

        /**
         * Make a button for the function curves.
         * TODO: move this to Dyn.plotUtil and use for both the MapElement and PlotFunctionElement.
         * @param ioAdornArray
         * @param buttonNum
         * @param buttonTypeString
         * @param toolTipText
         */
        makeToolButton( ioAdornArray, buttonNum, buttonTypeString, toolTipText, path ) {
            "use strict";

            function buttonHoverIn( e ) {
                e.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
                this.attr( { fill: Dyn.plotUtil.mapButtonHover, stroke: Dyn.plotUtil.handleDownColor } );
                const toolTipOffset = 20;
                if( this.data( "tooltip" ) )
                    Dyn.toolTip.show( this, e, { x: this.attrs.x + toolTipOffset, y: this.attrs.y + toolTipOffset } );
            }

            function buttonHoverOut( e ) {
                e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                this.attr( { fill: Dyn.plotUtil.mapButtonNormal, stroke: Dyn.plotUtil.axisButtonUpColor } );
                if( Dyn.toolTip.isMyElement( this ) )
                    Dyn.toolTip.hide();
            }

            // function iconHoverIn( e ){
            //     this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: Dyn.plotUtil.axisButtonDownColor } );
            // }
            // function iconHoverOut( e ){
            //     this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: Dyn.plotUtil.axisButtonUpColor } );
            // }
            function buttonClick( /*e*/ ) {
                var thisButton = this,
                    buttonType = thisButton.data( "type" );
                mediator.publish( "axis:clickButton", { type: buttonType } );
            }

            var plotElement = this,
                plotPaper = this.plotShaper.adornments.paper,
                isActiveTool = false, // todo: caller should determine this.
                ariaLabel = toolTipText,
                left = Dyn.plotUtil.mapButtonleft,
                top = this.buttons.top( buttonNum ),
                set = plotPaper.set(),
                outline = plotPaper.rect( left, top, this.buttons.width, this.buttons.height, this.buttons.radius )
                    .attr( {
                        stroke: plotElement.getOutlineStrokeColor( isActiveTool ),
                        fill: Dyn.plotUtil.mapButtonNormal
                    } )
                    .data( "type", buttonTypeString )
                    .data( "tooltip", toolTipText )
                    //.hover( buttonHoverIn, buttonHoverOut )
                    .click( buttonClick ),
                symbol = plotPaper.path( Raphael.transformPath( path, "T" + (left) + "," + (top) ) )
                    .attr( { stroke: "none", fill: plotElement.getSymbolFillColor( isActiveTool ) } );
            // .hover( iconHoverIn, iconHoverOut );
            outline.node.setAttribute( "role", "button" );

            // make element keyboard accessible.
            let keyDownHandler = function( e ) {
                switch( e.key ) {
                    case "Enter":
                        if( buttonClick ) {
                            buttonClick.call( outline, e );
                        }
                        break;
                }
            };
            Dyn.plotUtil.setKeyboardFocusable( outline, keyDownHandler, !!ariaLabel ? ariaLabel : false );

            // button is a set with an outline and a symbol
            if( !Dyn.environment.isTouchDevice() ) {
                outline.hover( buttonHoverIn, buttonHoverOut );
            }
            Dyn.plotUtil.disableMouseOverEvents( symbol ); // so it does not interfere with outline's buttonHoverIn()
            set.push( outline ); // [0]
            set.push( symbol );  // [1]
            return set;
        }

        getOutlineStrokeColor( isActiveTool ) {
            "use strict";
            return isActiveTool ? Dyn.plotUtil.axisButtonDownColor : Dyn.plotUtil.axisButtonUpColor;
        }

        getSymbolFillColor( isActiveTool ) {
            "use strict";
            return isActiveTool ? Dyn.plotUtil.axisButtonDownColor : Dyn.plotUtil.axisButtonUpColor;
        }

        /**
         * {OVERRIDE} Deselect any selected adornments in this plot element.
         */
        clearSelectedAdornments() {
            "use strict";
            let oldFormulaIndex = this.selectedFormulaIndex;

            this.selectedFormulaIndex = -1;

            // clear selection indicators in fx line and fx control panel
            if( Dyn.mathUtil.isInIntegerRange( oldFormulaIndex, 0, this.formulas.length ) ) {
                let oldFormulaID = this.formulas[oldFormulaIndex].id;
                this.showHidePointsOfSelectedLine();
                mediator.publish( "plot:plotFunctionSelected", { id: oldFormulaID, isSelected: false } );
            }
        }

        /**
         * Select an adornment.
         * @param adornmentIndex
         */
        selectAdornment( formulaIndex ) {
            // set selection indicators in fx line and fx control panel
            if( Dyn.mathUtil.isInIntegerRange( formulaIndex, 0, this.formulas.length ) ) {
                let formulaID = this.formulas[formulaIndex].id;
                if( formulaIndex !== this.selectedFormulaIndex ) {
                    this.selectedFormulaIndex = formulaIndex;
                    // if showing squares on lines, setShowSquares(true) for this element, setShowSquares(false) for others
                    this.plotShaper.showHideSquaresOnLines( this.plotShaper.showSquaresOnLines, this );
                    this.plotShaper.clearSelectedAdornments( this ); // clear other adornment selections
                    this.showHidePointsOfSelectedLine(); // select points on this line
                    // tell formula panel to highlight the formula for the selected path
                    mediator.publish( "plot:plotFunctionSelected", { id: formulaID, isSelected: true } );
                }
            } else {
                this.clearSelectedAdornments();
            }
        }

        /**
         * Show the points of the selected function line, if they exist and are valid,
         * and hide the points on unselected functions. */
        showHidePointsOfSelectedLine() {
            let adornmentsArray = this.getAdornmentsArray();

            this.formulas.forEach( ( fx, formulaIndex ) => {
                let isFormulaSelected = (formulaIndex === this.selectedFormulaIndex),
                    pathSet = this.paths.getPathSet( adornmentsArray, formulaIndex );
                for( let i=0; i<this.paths.numPointElements; i++ ) { // for all point elements in pathSet
                    let pointData = pathSet.getPointElement(i)?.data( "pointData" );
                    pointData?.updateVisibility( isFormulaSelected );
                }
            }, this );
        }

        /**
         * Set or update the given formula in the plot. See also removeFormula()
         * When plot is updated, this.calcAdornments() will add/update/delete the matching function line.
         * @param formulaID
         * @param formulaString
         * @param formulaSyntax
         * @param constants
         * @param lineColor
         * @param isSingleFunctionUpdate
         */
        setFormula( formulaID, formulaString, formulaSyntax, constants, lineColor, lineVisible, isSingleFunctionUpdate=false  ) {
            // IDs are strings, make it so, for consistency, before comparing or assigning to any formula
            // NOTE: ModelFunctions.jsx makes numeric IDs, but Dyn.FunctionUtil uses IDs as object keys which convert to strings.
            formulaID = "" + formulaID;
            let isUpdate = false,
                formulaIndex = this.formulaIDtoIndex( formulaID );

            // check parameters
            if( Dyn.mathUtil.isInIntegerRange( formulaIndex, 0, this.formulas.length ) ) {
                isUpdate = true;
            }
            if( !constants ) {
                constants = {};
            }
            if( !constants.hasOwnProperty( 'x' ) ) {
                constants.x = NaN; // define X for syntax checking
            }

            Dyn.assert( formulaSyntax==='LaTeX' || !formulaSyntax);
            let newFormula = new Dyn.PlotFormula( formulaString || "", constants, formulaID, formulaSyntax, lineVisible );
            newFormula.setLineColor( lineColor );

            if( isUpdate ) {
                let oldFormula = this.formulas[formulaIndex];

                this.isUpdateToConstantsOnly = (newFormula.formulaString === oldFormula.formulaString && newFormula.lineColor === oldFormula.lineColor);
                this.isSingleFunctionUpdate = Boolean( isSingleFunctionUpdate );
                this.singleFunctionUpdateID = formulaID;
                this.formulas[formulaIndex] = newFormula;   // update old formula with new uncompiled formula
            } else {
                this.isUpdateToConstantsOnly = this.isSingleFunctionUpdate = false;
                this.formulas.push( newFormula );           // add additional formula
            }
            //console.log( "PlotFunctionElement.setFormula() "+formulaIndex+" of "+this.formulas.length+" '"+formulaString+"' => '"+newFormula.formulaString+"'", constants, lineColor );
        }

        /**
         * Remove the given formula and return the resulting number of formulas left.
         * @param formulaID
         * @returns {number}
         */
        removeFormula( formulaID ) {
            let formulaIndex = this.formulaIDtoIndex( formulaID );

            if( Dyn.mathUtil.isInIntegerRange( formulaIndex, 0, this.formulas.length ) ) {
                this.formulas.splice( formulaIndex, 1 );        // remove a formula
                this.labelOffsets.splice( formulaIndex, 1 );    // remove its label offset
                if( this.selectedFormulaIndex >= formulaIndex ) {
                    this.selectedFormulaIndex--; // adjust index to match (1 => 0, 0 => -1, etc)
                }
                if( this.showSquaresFormulaIndex >= formulaIndex ) {
                    this.showSquaresFormulaIndex--; // adjust index to match (1 => 0, 0 => -1, etc)
                }
                //console.log( "PlotFunctionElement.setFormula() remove index:", formulaIndex, ", now ", this.formulas.length, " formulas" );
            }
            return this.formulas.length;
        }

        getFormulaMessage( formulaIndex ) {
            let formulaObj = this.formulas[formulaIndex],
                formulaRange = this.formulaRanges[formulaIndex],
                result = {
                    id: formulaObj.id,
                    message: "", // empty string if no errors
                    rangeX: "",
                    rangeY: "",
                    warning: "",
                    fullString: ""
                },
                formulaNotVisible = (
                    (this.plotRange.dataX.isFinite() && this.plotRange.dataY.isFinite() &&
                        formulaRange.countFinite > 0) &&
                    (this.plotRange.dataX.min > formulaRange.evalX.max ||
                        this.plotRange.dataX.max < formulaRange.evalX.min ||
                        this.plotRange.dataY.min > formulaRange.evalY.max ||
                        this.plotRange.dataY.max < formulaRange.evalY.min));

            function resultToString( result ) {
                // compare this string to old state to know if state has changed
                return result.id + "|" + result.message + "|" + result.rangeX + "|" + result.rangeY + "|" + result.warning;
            }

            // add formula's computed numeric range if any
            if( formulaRange ) {
                result.rangeX = formulaRange.stringX;
                result.rangeY = formulaRange.stringY;
            }

            // add formula's error message if any
            if( formulaObj ) {
                result.message = formulaObj.getLastErrorString( "notEvalErrors" );
            }
            if( !result.message ) {
                // if no other error, report that functions are not plottable
                if( !this.isInPlottableAxes ) {
                    result.message = Dyn.loc( "Need numeric axis on X and Y to plot functions" );
                } else if( formulaRange &&
                    (formulaRange.countAll > 0) && (formulaRange.countFinite === 0) ) {

                    result.message = Dyn.loc( "Formula is undefined across X-axis range" );
                }
            }
            // if formula is correct and there is plot function on the visualization, check if its in the
            // visible area, if not send the warning message.
            if( formulaNotVisible ) {
                result.warning = Dyn.loc( "The function is outside the plot viewing area." );
            }

            result.fullString = resultToString( result );

            return result;
        }

        getNumFormulas() {
            return this.formulas.length;
        }

        /**
         * convert a formula ID to a valid index into our list of formulas, or return the next available formula index
         * @param formulaID -- number or string
         * @param allowFailure -- if true, return -1 if not found, instead of next available formula index
         * @returns {number}
         */
        formulaIDtoIndex( formulaID, allowFailure ) {
            // IDs are strings, make it so, for consistency, before comparing or assigning to any formula
            formulaID = "" + formulaID;
            var formulaIndex = this.formulas.findIndex( formulaObj => (formulaObj.id === formulaID) );
            return ((formulaIndex === -1 && !allowFailure) ?
                this.formulas.length    // not found; return next available index, 0+
                : formulaIndex);       // return index of formula with matching ID
        }

        /**
         * Initialize the data and function evaluation ranges.
         * To be used to reset the ranges after every data or function change.
         * Caller should use this.plotRange.evalY.addPoint(), etc to expand the ranges once a finite
         *      numeric value has been calculated.
         * @param xAxisView
         * @param yAxisView
         */
        initRange( xAxisView, yAxisView ) {
            this.plotRange = new PlotRange( xAxisView, yAxisView );
            this.formulaRanges = [];
            this.formulas.forEach( ( formula, index ) => {
                this.formulaRanges[index] = new FormulaRange();
            } )
        }

        /**
         * reset the axes to show all the function x,y points, or all x,y data points (toggle).
         * Note that the function is evaluated across the current X-axis range, so that after a
         * formula change this reset will typically only change the Y axis.
         */
        resetXYAxisRanges( subViewRef, type ) {
            "use strict";
            var xAxisView = subViewRef.xAxisView,
                yAxisView = subViewRef.yAxisView,
                userRangeX = xAxisView.numeric.userRange || xAxisView.getNumericAxisRange(), // the last user-specified range (final axis range not the sa
                userRangeY = yAxisView.numeric.userRange || yAxisView.getNumericAxisRange();

            switch( type ) {
                case "resetToData": {
                    // reset to show all XY data points
                    xAxisView.setUserCustomizedMinMax();
                    yAxisView.setUserCustomizedMinMax();
                }
                    break;
                case "resetToEval": {
                    xAxisView.setUserCustomizedMinMax( this.plotRange.evalX ); // reset to show all XY points of evaluated formula
                    yAxisView.setUserCustomizedMinMax( this.plotRange.evalY ); // reset to show all XY points of evaluated formula
                }
                    break;
                case "axisPlus": {
                    const zoomInFraction = 0.25;
                    let wx = userRangeX.width() * zoomInFraction,
                        wy = userRangeY.width() * zoomInFraction;
                    userRangeX.set( userRangeX.min + wx, userRangeX.max - wx );
                    userRangeY.set( userRangeY.min + wy, userRangeY.max - wy );
                    xAxisView.setUserCustomizedMinMax( userRangeX );
                    yAxisView.setUserCustomizedMinMax( userRangeY );
                }
                    break;
                case "axisMinus": {
                    const zoomOutFraction = 0.5;
                    let wx = userRangeX.width() * zoomOutFraction,
                        wy = userRangeY.width() * zoomOutFraction;
                    userRangeX.set( userRangeX.min - wx, userRangeX.max + wx );
                    userRangeY.set( userRangeY.min - wy, userRangeY.max + wy );
                    xAxisView.setUserCustomizedMinMax( userRangeX );
                    yAxisView.setUserCustomizedMinMax( userRangeY );
                }
                    break;
            }
        }

// hw mess    Dyn.EPlotChange parameter if returning true.
// hw mess     * @param subViewRef
// hw mess     * @param axisdata
// hw mess     * @returns {boolean} -- true if handled by a Dyn.PlotElement
// hw mess     */
        handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
            "use strict";
            var handled = false;

            switch( axisData.type ) {
                case "resetToData":
                case "resetToEval":
                case "axisPlus":
                case "axisMinus":
                    if( this.isInPlottableAxes ) {
                        this.resetXYAxisRanges( subViewRef, axisData.type );
                        ioWhatChanged.viewSize = true; // update plot to new axis position
                        handled = true;
                    }
                    break;
            }
            return handled;
        }

        /**
         * Tell plot elements to hide or show any controls.
         */
        setViewMode( mode ) {
            // NOTE: buttons may be null, that is OK
            let showControls = mode.showControls();
            Dyn.plotUtil.showOrHideElement( this.buttonPlus, showControls );
            Dyn.plotUtil.showOrHideElement( this.buttonMinus, showControls );
            Dyn.plotUtil.showOrHideElement( this.buttonReset, showControls );
            Dyn.plotUtil.showOrHideElement( this.buttonFit, showControls );
        }


        /** create the SVG elements for "Show Label": label rectangle, text, and connector line */
        initLabelElements( pathSet, /*hoverInHandler, hoverOutHandler,*/ formulaIndex ) {
            let Dyn = this.dyn,
                plotElement = this,
                plotViewPaper = this.plotShaper.adornments.paper;

            let labelConnectorEl = plotViewPaper.path( "M0,0" );
            let labelRectElement = plotViewPaper.rect( 0, 0, 0, 0, Dyn.plotUtil.statLabelRoundRectRadius );
                // labelRectElement.hover( hoverInHandler, hoverOutHandler );

            // init label text element as a mathQuill container for rendering Latext math formulas
            let labelTextElement = plotViewPaper.foreignObject("", 0, 0, 100, 100 )
                .attr( {
                    'stroke-width': 1,
                    'text-anchor': 'middle',
                    'font-size': Dyn.preferences.getFontSize(),
                    'font-family': Dyn.plotUtil.statLabelFontFamily
                });

            // init child html element to hold the mathQuill text
            const body = labelTextElement.node.firstChild;
            const textContainer = document.createElement('div');
            body.appendChild(textContainer);
            body.style.textAlign = 'center';
            const MQ = window.MathQuill.getInterface(2);
            labelTextElement.setLatex = function( text, renderCallback) {
                // handler to set the Latex-style math formula
                labelTextElement.attr( 'font-size', Dyn.preferences.getFontSize() );
                textContainer.innerText = text;
                let mqStatic = MQ.StaticMath(textContainer);
                // labelTextElement.data("mqStatic",mqStatic);
                setTimeout( renderCallback, 100); // do after MathQuill has rendered
            }

            // add drag and key handlers to the label elements
            labelRectElement.drag( labelDragMove.bind( pathSet ), labelDragStart.bind( pathSet ), labelDragEnd.bind( pathSet ) );
            Dyn.plotUtil.disableMouseOverEvents( labelConnectorEl );
            Dyn.plotUtil.disableMouseOverEvents( labelTextElement );
            addKeyHandlers( labelRectElement, labelDragStart, labelDragMove, labelDragEnd,
                Dyn.PlotLabelsUtil.getAriaLabelForStatLabel( Dyn.loc("Function {{n}}", { n: formulaIndex+1 }) ) );
            this.plotShaper.setAdornmentLayer( 1, labelRectElement, labelTextElement, labelConnectorEl ); // stat labels above other stat lines

            // return new elements; caller adds to PathSet and calls updateLabelElements
            return [ labelConnectorEl, labelRectElement, labelTextElement ];

            function labelDragMove( dx, dy, x, y, event ) {
                // Note: dx and dy are positions relative to starting position (not last dragMove)
                if( plotElement.ignoreDragEvents() )
                    return;
                Dyn.assert( pathSet ); // from closure
                let labelConnectorEl = pathSet.getLabelConnectorEl(),
                    labelRectElement = pathSet.getLabelRectEl(),
                    labelTextElement = pathSet.getLabelTextEl(),
                    widgetRect = labelTextElement.data( 'widgetRect' ),
                    formulaIndex = labelTextElement.data( "formulaIndex" ),
                    labelTextX = labelTextElement.data( "startX" ) + dx,
                    labelTextY = labelTextElement.data( "startY" ) + dy;

                // move the label text and label rect positions
                labelTextElement.attr( { x: labelTextX, y: labelTextY } );
                labelRectElement.attr( { x: labelTextX - Dyn.plotUtil.statLabelBorderDx, y: labelTextY - Dyn.plotUtil.statLabelBorderDy } );
                // update the connector line
                let labelRect = Dyn.PlotLabelsUtil.getLabelRectFromTextElement( labelTextElement );
                plotElement.updateLabelConnectPt( widgetRect, labelRect.centerX() );
                let connectorLineRect = Dyn.PlotLabelsUtil.getLineFromLabelToFxLine( labelRect, widgetRect.connectPt, widgetRect.rangeX, widgetRect.wantHorizontalLine );
                labelConnectorEl.attr( { path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ) } );

                // save the new label position as an offset from the widget position
                plotElement.labelPositionToModel( formulaIndex, labelTextElement, labelRect, widgetRect );
                plotElement.selectAdornment( formulaIndex );
            }

            function labelDragStart( x, y, event ) {
                if( plotElement.ignoreDragEvents() )
                    return;
                Dyn.assert( pathSet ); // from closure
                let labelTextElement = pathSet.getLabelTextEl(),
                    labelRectElement = pathSet.getLabelRectEl();
                labelTextElement.data( "startX", labelTextElement.attr( "x" ) );
                labelTextElement.data( "startY", labelTextElement.attr( "y" ) );
                labelRectElement.attr( "cursor", Dyn.plotUtil.cursorMove );
            }

            function labelDragEnd( event ) {
                if( plotElement.ignoreDragEvents() )
                    return;
                Dyn.assert( pathSet ); // from closure
                let labelRectElement = pathSet.getLabelRectEl();
                Dyn.plotUtil.setAsKeyboardFocus( labelRectElement );
            }

            function addKeyHandlers( elementToFocus, dragStart, dragMove, dragEnd, ariaLabel ) {
                function keyDownHandler( e ) {
                    switch( e.key ) { // const {key} = event; ES6+
                        case "ArrowLeft":
                        case "ArrowRight":
                        case "ArrowUp":
                        case "ArrowDown": {   // move this element one pixel in arrow's direction
                            let dist = Dyn.plotUtil.arrowKeyAcceleratedDistance( e.key ),
                                x, y /* placeholders -- x,y not used by drag handlers */,
                                element = this;
                            dragStart.call( element, x, y, e );
                            dragMove.call( element, dist.dx, dist.dy, x, y, e );
                            dragEnd.call( element, e );
                        }
                            break;
                    }
                }
                Dyn.plotUtil.setKeyboardFocusable( elementToFocus, keyDownHandler.bind(elementToFocus), ariaLabel);
            }

        }

        /** update the label text and position, and the connector line to the function curve */
        updateLabelElements( pathSet, formulaObj, formulaIndex, formulaRange, scope, xAxisView, yAxisView, animationTime, animationEasing ) {
            // create a widgetRect to hold the required rectangle for PlotLabelsUtil functions
            // widgetRect is always a point (height and width are 0) that label connects to, see getLineFromLabelToFxLine()
            let widgetRect = new Dyn.mathUtil.Rect( 0, 0, 0, 0 );
            widgetRect.rangeX = formulaRange.viewX; // for getLineFromLabelToFxLine()
            widgetRect.formulaObj = formulaObj;
            widgetRect.formulaIndex = formulaIndex;
            widgetRect.scope = scope;
            widgetRect.xAxisView = xAxisView;
            widgetRect.yAxisView = yAxisView;
            widgetRect.wantHorizontalLine = false;
            widgetRect.pathColor = formulaObj.lineColor;

            // restore saved label position offset from model to labelTextElement
            const cell = this.plotShaper.bins.cells[0];
            let labelTextElement = pathSet.getLabelTextEl();
            this.modelToLabelPosition( labelTextElement, cell, widgetRect.formulaIndex );

            // get or calculate the connection point on the curve, update widgetRect
            let connectViewX = this.getOrInitializeLabelConnectPt( formulaObj, formulaIndex, formulaRange, xAxisView );
            this.updateLabelConnectPt( widgetRect, connectViewX );

            // update the label text element with the formula, render it
            labelTextElement.setLatex( formulaObj.latexString, () => {
                // callback to set the label position after MQ render
                let labelRect = this.updateLabelCallback( pathSet, widgetRect, animationTime, animationEasing );
                // label position may have changed again, by Dyn.PlotLabelsUtil.setLabelPosition() which avoids overlaps
                // this.updateLabelConnectPt( widgetRect, labelRect.centerX(), labelTextElement, formulaIndex ); // removed for #2710
                this.labelPositionToModel( widgetRect.formulaIndex, labelTextElement, labelRect, widgetRect );
            })
        }

        /** get the x-axis data point that the label is connected to, or initialize it if not set. */
        getOrInitializeLabelConnectPt( formulaObj, formulaIndex, formulaRange, xAxisView ) {
            let connectDataX = this.labelOffsets[formulaIndex]?.connectDataX, // saved from last updateLabelElements()
                connectViewX;
            if( Number.isFinite( connectDataX ) ) {
                connectViewX = xAxisView.coordMap.transform( connectDataX );
            } else {
                let widthFraction = ((formulaIndex+1) / (formulaIndex+2)) * 1.7 - 0.6; // initial placement fraction: .25, .53, .67, etc
                connectViewX = formulaRange.viewX.min + (formulaRange.viewX.width() * widthFraction);
            }
            return connectViewX;
        }

        /** update the point on the curve that label is connected to, from an x-axis view coordinate.
         *  As the label position changes, we also want to update the connection point on the curve. */
        updateLabelConnectPt( widgetRect, connectViewX ) {
            let formulaObj = widgetRect.formulaObj,
                dataX = widgetRect.xAxisView.coordMap.inverseTransform( connectViewX );
            formulaObj.getPlotFunctionScope( widgetRect.scope, dataX );
            let dataY = formulaObj.evaluate( widgetRect.scope, 'number' ),
                curvePt = new FunctionPointData( dataX, dataY );
            curvePt.updateViewCoords( widgetRect.xAxisView, widgetRect.yAxisView );
            widgetRect.connectDataX = dataX; // save for next updateLabelElements()
            widgetRect.x = curvePt.viewX;
            widgetRect.y = curvePt.viewY;
            widgetRect.connectPt = { x: curvePt.viewX, y: curvePt.viewY }; // used by getLineFromLabelToFxLine()
        }

        /** update the label position and connector line for the formula; see initLabelElements() */
        updateLabelCallback( pathSet, widgetRect, animationTime, animationEasing ) {
            const cell = this.plotShaper.bins.cells[0];
            let labelRectEl = pathSet.getLabelRectEl(),
                labelTextElement = pathSet.getLabelTextEl(),
                labelConnectorEl = pathSet.getLabelConnectorEl();
            let labelRect = Dyn.PlotLabelsUtil.setLabelPosition( this, labelTextElement, widgetRect.wantHorizontalLine, cell, widgetRect ),
                connectorLineRect = Dyn.PlotLabelsUtil.getLineFromLabelToFxLine( labelRect, widgetRect.connectPt, widgetRect.rangeX, widgetRect.wantHorizontalLine ),
                clipRectString = Dyn.plotView.getPlotInteriorClipRect();
            labelTextElement.data('formulaIndex', widgetRect.formulaIndex );
            labelRectEl.animate( {
                x: labelRect.x,
                y: labelRect.y,
                width: labelRect.width,
                height: labelRect.height,
                fill: Dyn.plotUtil.statLabelTextColor,
                stroke: widgetRect.pathColor,
                'clip-rect':clipRectString,
            }, animationTime, animationEasing );
            let textRect = Dyn.PlotLabelsUtil.getTextRectFromLabelRect( labelRect );
            labelTextElement.animate( {
                x: textRect.x, y: textRect.y, width: textRect.width, height: textRect.height,
                fill: Dyn.plotUtil.titleColor,
                'font-size':Dyn.preferences.getFontSize(),
                'clip-rect':clipRectString,
            }, animationTime, animationEasing );
            labelConnectorEl.animate( {
                stroke: widgetRect.pathColor,
                path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ),
                'clip-rect':clipRectString,
            }, animationTime, animationEasing );
            return labelRect;
        }

        /** transfer saved label position offsets from labelTextElement to model for save/restore */
        labelPositionToModel( formulaIndex, labelTextElement, labelRect, widgetRect ) {
            // Dyn.PlotLabelsUtil.saveLabelPositionAndOffset( labelTextElement, labelRect, widgetRect ); // removed for #2710
            let labelOffset = labelTextElement.data( "offset" ); // from saveLabelPositionAndOffset()
            if( labelOffset ){
                // save relative position center of user-dragged label from Fx Line
                this.labelOffsets[formulaIndex] = {
                    ox: labelOffset.x.n,
                    oy: labelOffset.y.n,
                };
                if( Number.isFinite( widgetRect.connectDataX ) ) {
                    // also save the X position of the connection point on the function curve, in data coords
                    this.labelOffsets[formulaIndex].connectDataX = widgetRect.connectDataX;
                }
            }
        }

        /** restore any normalized positions that were saved, else do nothing */
        modelToLabelPosition( labelTextElement, cellRect, formulaIndex ) {
            let labelOffset = labelTextElement.data( "offset" ) || {};
            if( this.labelOffsets && this.labelOffsets[formulaIndex] ){
                // restore the relative offset from widget to user-dragged label,
                //  which is used by PlotLabelsUtil.restoreLabelPositionAndOffset()
                let nc = this.labelOffsets[formulaIndex];
                labelOffset.x = new Dyn.axisUtil.NACoord( nc.ox );
                labelOffset.y = new Dyn.axisUtil.NACoord( nc.oy );
                // save for restoreLabelPositionAndOffset()
                labelTextElement.data( "offset", labelOffset );
                labelTextElement.data( "cellRect", cellRect );
                labelTextElement.data( "formulaIndex", formulaIndex );
            }
        }

        /**
         * @param wantToShowSquares {Boolean}
         */
        setShowSquares( wantToShowSquares ) {
            if( wantToShowSquares ){
                // if we want to show squares, indicate which of 1+ lines should show squares
                this.showSquaresFormulaIndex = (this.selectedFormulaIndex >= 0) ? this.selectedFormulaIndex : 0;
            } else { // else turn off show squares
                this.showSquaresFormulaIndex = -1;
            }
        }

        /** Does this plot element have lines that can show squares? */
        canShowSquares( requireSelectedLine ) {
            Dyn.assert( this[Dyn.EPlotControl.kControlsShowSquares]);
            let numLines = this.formulas.length,
            can = ( requireSelectedLine ?
                ((numLines > 0) && Dyn.mathUtil.isInIntegerRange( this.selectedFormulaIndex, 0, numLines)) :
                (numLines > 0));
            return can;
        }

        /**
         * Add or remove "show squares" squares to the given function; lines are elements added to squaresSet Raphael set.
         */
        updateShowSquares( showSquares, squaresData, xAxisView, yAxisView, squaresSet, pathElement, pathColor ) {
            Dyn.assert( showSquares ? this.plotShaper.showSquaresOnLines : true, "show squares only if 'Show Squares' is on" );
            let filter = squaresData.filter,
                squaresModel = new Dyn.SquaresOnLineModel(
                    xAxisView, yAxisView, 0, 0, squaresData /* line equation */, filter.params, squaresSet, pathColor );
            squaresModel.updateSquares( showSquares, filter.caseFilter, this, pathElement );
        }

        /** update the sum of squares value in squaresData, if showing squares in any line */
        updateSumOfSquares( showSumOfSquares, squaresData, xAxisView, yAxisView ) {
            if( showSumOfSquares ) {
                // add sum of squares to the tooltip text and Summary View
                let xAttr = xAxisView.getAttribute(),
                    yAttr = yAxisView.getAttribute(),
                    filter = squaresData.filter,
                    ss = Dyn.plotUtil.calcSumOfSquares( xAttr, yAttr, filter.caseFilter, filter.params, squaresData );
                squaresData.sumOfSquares = yAttr.axisValueToString( ss ); // formatted for display
                // squaresData.tipText = Dyn.loc("sum of squares = {{sumOfSquares}}", squaresData );
            }
        }

    } // class PlotFunctionElement
    Dyn.PlotFunctionElement = PlotFunctionElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPlotFunction, Dyn.PlotFunctionElement );
} // wrap DynPlotFunctionElement
